import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Paper,
    Typography,
    Grid,
    Avatar,
    Tab,
    Tabs,
    Card,
    CardContent,
    Divider,
    Alert,
    CircularProgress,
    Chip,
    Button,
} from '@mui/material';
import {
    Person as PersonIcon,
    ShoppingBag as OrdersIcon,
    Favorite as FavoriteIcon,
    Verified as VerifiedIcon,
    Store as StoreIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import marketplaceService from '../services/marketplaceService';

const ProfilePage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState(0);

    // Fetch marketplace profile (id, username, email, full_name, company, is_verified, is_admin)
    const { data: profile, isLoading, error } = useQuery({
        queryKey: ['marketplaceProfile'],
        queryFn: () => marketplaceService.getMarketplaceProfile(),
    });

    // Fetch saved count for stats card
    const { data: savedItems = [] } = useQuery({
        queryKey: ['savedListings'],
        queryFn: () => marketplaceService.getSaved(),
    });

    // Fetch inventory stats for seller stats
    const { data: inventoryStats } = useQuery({
        queryKey: ['inventoryStats'],
        queryFn: () => marketplaceService.getInventoryStats(),
    });

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">Failed to load profile. Please try again.</Alert>
            </Container>
        );
    }

    const displayName = profile?.full_name || profile?.username || user?.username || 'User';
    const initials = displayName.charAt(0).toUpperCase();

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Profile Header */}
            <Paper sx={{ p: 4, mb: 3, borderRadius: 3 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: '2.5rem' }}>
                            {initials}
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h4" fontWeight={600}>
                                {displayName}
                            </Typography>
                            {profile?.is_verified && (
                                <Chip
                                    icon={<VerifiedIcon />}
                                    label="Verified"
                                    color="success"
                                    size="small"
                                />
                            )}
                            {profile?.is_admin && (
                                <Chip label="Admin" color="error" size="small" />
                            )}
                        </Box>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                            @{profile?.username || user?.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {profile?.email}
                        </Typography>
                        {profile?.company && (
                            <Typography variant="body2" color="text.secondary">
                                🏢 {profile.company}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <Button variant="outlined" color="error" onClick={handleLogout}>
                            Log Out
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FavoriteIcon color="error" sx={{ fontSize: 40 }} />
                                <Box>
                                    <Typography variant="h4" fontWeight={600}>
                                        {savedItems.length}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">Favorites</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {inventoryStats && (
                    <>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <StoreIcon color="primary" sx={{ fontSize: 40 }} />
                                        <Box>
                                            <Typography variant="h4" fontWeight={600}>
                                                {inventoryStats.active_components}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">Active Listings</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <OrdersIcon color="primary" sx={{ fontSize: 40 }} />
                                        <Box>
                                            <Typography variant="h4" fontWeight={600}>
                                                {inventoryStats.total_available_quantity?.toLocaleString() || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">Units Available</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ fontSize: '2.5rem' }}>💰</Box>
                                        <Box>
                                            <Typography variant="h5" fontWeight={600}>
                                                ${inventoryStats.total_inventory_value?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">Inventory Value</Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>

            {/* Tabs */}
            <Paper sx={{ borderRadius: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
                >
                    <Tab icon={<PersonIcon />} label="Account Info" iconPosition="start" />
                    <Tab icon={<FavoriteIcon />} label="Favorites" iconPosition="start" />
                    <Tab icon={<StoreIcon />} label="My Listings" iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {/* Account Info Tab */}
                    {activeTab === 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight={600}>Account Information</Typography>
                            <Divider sx={{ mb: 3 }} />
                            <Grid container spacing={2}>
                                {[
                                    { label: 'Full Name', value: profile?.full_name || '—' },
                                    { label: 'Username', value: `@${profile?.username || '—'}` },
                                    { label: 'Email', value: profile?.email || '—' },
                                    { label: 'Company', value: profile?.company || '—' },
                                    { label: 'Account Type', value: profile?.is_admin ? 'Administrator' : 'Seller / Buyer' },
                                    { label: 'Verification', value: profile?.is_verified ? '✅ Verified' : '⏳ Not verified' },
                                ].map(({ label, value }) => (
                                    <Grid item xs={12} sm={6} key={label}>
                                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                                        <Typography variant="body1" fontWeight={500}>{value}</Typography>
                                    </Grid>
                                ))}
                            </Grid>
                            <Alert severity="info" sx={{ mt: 3 }}>
                                To update your profile details, please contact support or update via the main PartzFlow app.
                            </Alert>
                        </Box>
                    )}

                    {/* Favorites Tab */}
                    {activeTab === 1 && (
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight={600}>Saved Items</Typography>
                            <Divider sx={{ mb: 3 }} />
                            {savedItems.length > 0 ? (
                                <Typography variant="body1">
                                    You have {savedItems.length} saved {savedItems.length === 1 ? 'item' : 'items'}.{' '}
                                    <Button
                                        variant="text"
                                        onClick={() => navigate('/favorites')}
                                        sx={{ textTransform: 'none', p: 0 }}
                                    >
                                        View all favorites →
                                    </Button>
                                </Typography>
                            ) : (
                                <Alert severity="info">
                                    You haven't saved any components yet.{' '}
                                    <Button variant="text" onClick={() => navigate('/parts')} sx={{ textTransform: 'none', p: 0 }}>
                                        Start browsing →
                                    </Button>
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* My Listings Tab */}
                    {activeTab === 2 && (
                        <Box>
                            <Typography variant="h6" gutterBottom fontWeight={600}>My Component Listings</Typography>
                            <Divider sx={{ mb: 3 }} />
                            <Button variant="contained" onClick={() => navigate('/parts')} sx={{ borderRadius: 3 }}>
                                Manage My Listings
                            </Button>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default ProfilePage;
